/**
 * iPad: technician and customer signatures on the job's form. The certification statement is
 * shown above the signature line for the Field Report and Lube Report. The PNG is queued like a
 * photo, then the signature item that points at it — so signing works with no signal.
 */
import { useRef, useState } from 'react';
import { SIGNATURE_FORM_LABELS, type SignatureRole } from '@wo/shared';
import { SignatureBox, type SignatureHandle } from '../components/SignatureBox';
import { enqueueFile, enqueueItem } from './queue';
import type { FormLists, JobsPayload, MechForms, MechJob } from './types';
import { Big, BigField, Sheet } from './ui';

type SetForms = (fn: (f: MechForms) => MechForms) => void;

export function SignSheet({ job, payload, lists, userId, setForms, onClose }: { job: MechJob; payload: JobsPayload; lists: FormLists; userId: number; setForms: SetForms; onClose: () => void }) {
  const [role, setRole] = useState<SignatureRole | null>(null);
  const form = job.forms.signatureForm;
  const sigs = job.forms.signatures;
  const canSign = job.status === 'ASSIGNED' || job.status === 'IN_PROGRESS' || job.status === 'ON_HOLD' || job.status === 'READY_FOR_REVIEW';
  const certified = form === 'FIELD_REPORT' || form === 'LUBE';

  if (role) {
    return (
      <Pad
        title={role === 'TECHNICIAN' ? `Technician signature — ${payload.me.name}` : 'Customer signature'}
        statement={certified ? lists.certification.text : null}
        needName={role === 'CUSTOMER'}
        onCancel={() => setRole(null)}
        onSave={async (blob, signerName) => {
          const fileClientId = await enqueueFile(userId, `Signature #${job.number}`, blob, { workOrderId: job.id, kind: 'SIGNATURE', purpose: 'signature' });
          await enqueueItem(userId, `Signature #${job.number}`, { type: 'signature', workOrderId: job.id, form, role, fileClientId, ...(signerName ? { signerName } : {}) });
          const entry = { signerName: signerName ?? payload.me.name, signedAt: new Date().toISOString() };
          setForms((f) => ({ ...f, signatures: { ...f.signatures, [role === 'TECHNICIAN' ? 'technician' : 'customer']: entry } }));
          setRole(null);
        }}
      />
    );
  }

  return (
    <Sheet title={`Signatures — ${SIGNATURE_FORM_LABELS[form]}`} onClose={onClose}>
      <div className="grid gap-3">
        <SigRow label="Technician" done={sigs.technician} />
        <Big tone={sigs.technician ? 'light' : 'go'} className={sigs.technician ? 'border-2 border-slate-300' : ''} disabled={!canSign} onClick={() => setRole('TECHNICIAN')}>
          {sigs.technician ? 'Sign again (technician)' : 'Technician signs'}
        </Big>
        <SigRow label="Customer" done={sigs.customer} notPresent={sigs.customerNotPresent} />
        <Big tone={sigs.customer ? 'light' : 'go'} className={sigs.customer ? 'border-2 border-slate-300' : ''} disabled={!canSign} onClick={() => setRole('CUSTOMER')}>
          {sigs.customer ? 'Customer signs again' : 'Hand the iPad to the customer'}
        </Big>
        <p className="text-base text-slate-600">If the customer isn't there, tell the Manager — only the Manager can record "Customer not present".</p>
      </div>
    </Sheet>
  );
}

function SigRow({ label, done, notPresent }: { label: string; done: { signerName: string; signedAt: string } | null; notPresent?: boolean }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-slate-100 p-3 text-lg">
      <span className={`text-2xl font-black ${done ? 'text-green-700' : 'text-red-700'}`}>{done ? '✓' : '✗'}</span>
      <span className="flex-1">
        <b>{label}</b>
        {done ? ` — ${done.signerName}, ${new Date(done.signedAt).toLocaleString()}` : notPresent ? ' — Manager recorded "Customer not present"' : ' — not signed'}
      </span>
    </div>
  );
}

function Pad({ title, statement, needName, onCancel, onSave }: { title: string; statement: string | null; needName: boolean; onCancel: () => void; onSave: (png: Blob, name: string | null) => void }) {
  const pad = useRef<SignatureHandle>(null);
  const [empty, setEmpty] = useState(true);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white p-4">
      <h2 className="text-2xl font-bold">{title}</h2>
      {statement && <p className="my-2 max-h-40 overflow-y-auto text-base leading-snug text-slate-700">{statement}</p>}
      <SignatureBox ref={pad} height={300} onChange={setEmpty} className="my-2" />
      <p className="text-base text-slate-600">Sign in the box with a finger.</p>
      {needName && <BigField label="Printed name" value={name} onChange={(e) => setName(e.target.value)} autoCapitalize="words" />}
      <div className="mt-auto grid grid-cols-3 gap-3 pt-4">
        <Big tone="light" className="border-2 border-slate-300" onClick={onCancel}>Cancel</Big>
        <Big tone="light" className="border-2 border-slate-300" onClick={() => pad.current?.clear()}>Clear</Big>
        <Big tone="go" disabled={empty || busy || (needName && name.trim().length < 2)} onClick={async () => {
          setBusy(true);
          try {
            onSave(await pad.current!.toBlob(), needName ? name.trim() : null);
          } finally {
            setBusy(false);
          }
        }}>Done</Big>
      </div>
    </div>
  );
}
